import { createContext, useContext, useState } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import useSettingsParams from "hooks/useSettingsParams";

type WarehouseOperationType = {
    warehouseId?: number;
    productId?: number;
    stockCount?: number;
} | null;

type WarehouseContextType = {
    showSpinner: boolean;
    setShowSpinner: Dispatch<SetStateAction<boolean>>;
    stocksOrder: string | false;
    setStocksOrder: Dispatch<SetStateAction<string | false>>;
    stocksDesc: boolean;
    setStocksDesc: Dispatch<SetStateAction<boolean>>;
    wOperation: WarehouseOperationType;
    setWOperation: Dispatch<SetStateAction<WarehouseOperationType>>;
    stockFilter: Record<string, any> | null;
    setStockFilter: Dispatch<SetStateAction<Record<string, any> | null>>;
    showCredit: boolean;
    setShowCredit: Dispatch<SetStateAction<boolean>>;
    listResetNonce: number;
    triggerListReset: () => void;
    listRefreshNonce: number;
    triggerListRefresh: () => void;
};

const WarehouseContext = createContext<WarehouseContextType | null>(null);
export const useWarehouse = (): WarehouseContextType => {
    const context = useContext(WarehouseContext);
    if (!context) {
        throw new Error("useWarehouse must be used within WarehouseProvider");
    }
    return context;
};
 
export const WarehouseProvider = ({ children }: { children: ReactNode }) => {
    const params = useSettingsParams();
    const [ showSpinner, setShowSpinner ] = useState(false);
    const [ stocksOrder, setStocksOrder ] = useState<string | false>('');
    const [ stocksDesc, setStocksDesc ] = useState(false);
    const [ wOperation, setWOperation ] = useState<WarehouseOperationType>(null);
    const [ stockFilter, setStockFilter ] = useState<Record<string, any> | null>(null);
    const [ showCreditState, setShowCreditState ] = useState(false);
    const [ listResetNonce, setListResetNonce ] = useState(0);
    const [ listRefreshNonce, setListRefreshNonce ] = useState(0);

    const showCredit = Boolean(params.creditControl && showCreditState);
    const setShowCredit: Dispatch<SetStateAction<boolean>> = (next) => {
        if (!params.creditControl) return;
        setShowCreditState((prev) =>
            typeof next === 'function' ? Boolean(next(prev)) : Boolean(next),
        );
    };

    const triggerListReset = () => setListResetNonce((nonce) => nonce + 1);
    const triggerListRefresh = () => setListRefreshNonce((nonce) => nonce + 1);

    const values = {
        showSpinner, setShowSpinner,
        stocksOrder, setStocksOrder,
        stocksDesc, setStocksDesc,
        wOperation, setWOperation,
        stockFilter, setStockFilter,
        showCredit, setShowCredit,
        listResetNonce, triggerListReset,
        listRefreshNonce, triggerListRefresh,
    }

    return (
        <WarehouseContext.Provider value={values}>
            {children}
        </WarehouseContext.Provider>
    )
}
